import { inject, Injectable, Injector } from '@angular/core';
import { RequestsProvider } from '../requests/requests';

import moment from 'moment';
import { LoggerWrapper } from '../logger/logger-wrapper.service';
import { LoggerFactory } from '../logger/logger-factory.service';
import { AnalyticsProvider } from '../analytics/analytics';
import { ISynchronisationService } from './iSynchronisation-service';
import { UserService } from '../auth/user.service';
import { NetworkProvider } from '../network/network';
import { environment } from '../../../../environments/environment';
import { AgendaOtService } from '../agenda/agenda-ot.service';
import { AgendaTempoService } from '../agenda/agenda-tempo.service';
import { ActiviteInterneService } from '../activite-interne/activite-interne.service';
import { InitEntry } from '../requests/get-init-response.model';
import { ReleveP1SiteDto } from '../../../api/models/releve-p-1-site-dto';

/**
 * Synchronisation Provider
 *
 * Ce service permet d'accéder aux service de l'API Activités
 */
@Injectable({ providedIn: 'root' })
export class SynchronisationProvider {
  /**
   * Utilisé pour l'url de config de l'API Dactech Back
   *
   * @type {string}
   */
  apiUrlBack = '';

  /**
   * interval pour le check synchronisation
   *
   * @type {string}
   */
  interval: any;
  /**
   * Date de la derniere synchronisation
   *
   * @type {string}
   */
  lastSync: number;
  /**
   * Elements à synchroniser
   *
   * @type {string}
   */
  items: {
    title: string;
    lastSync: number | null;
    disabled: boolean;
    url_search: string;
    url: any[];
    count: number;
    count_childs?: number;
    site_ids?: any[];
    word: string;
    service?: ISynchronisationService;
  }[];
  /**
   * Elements dactech back à synchroniser
   *
   * @type {string}
   */
  items_releve_P1: {
    title: string;
    lastSync: number | null;
    disabled: boolean;
    url_search: string;
    url: any[];
    params_count: number;
    count: number;
    word: string;
  }[];

  /**
   * Logger
   */
  private logger: LoggerWrapper;

  /**
   * Constructeur
   *
   * Crée une instance du provider activités.
   *
   * @param {RequestsProvider} requestsProvider
   * @param userService
   * @param loggerFactory
   * @param analyticsProvider
   * @param networkProvider
   * @param injector
   */
  constructor(
    private requestsProvider: RequestsProvider,
    private userService: UserService,
    private loggerFactory: LoggerFactory,
    private analyticsProvider: AnalyticsProvider,
    private networkProvider: NetworkProvider,
    private injector: Injector
  ) {
    this.logger = this.loggerFactory.getInstance(this, true);
    this.apiUrlBack = environment.apis.API_DACTECH_BACK_V1;

    /**
     *  A terme, il faudra voir pour stocker ça dans le local storage pour ne pas
     *  perdre le lastSync
     */
    this.items = [
      {
        title: 'OT Vue Tournée',
        lastSync: null,
        disabled: true,
        url_search: '/ots?idIntervenant=',
        url: [],
        count: 0,
        word: 'OT(s) Tournee',
      },
      {
        title: 'OT Vue Agenda',
        lastSync: null,
        disabled: true,
        url_search: '/agendaOTs?id_intervenant=',
        url: [],
        count: 0,
        word: 'OT(s) Agenda',
        service: this.injector.get(AgendaOtService),
      },
      {
        title: 'Sites',
        lastSync: null,
        disabled: true,
        url_search: '/localisations',
        url: [],
        count: 0,
        count_childs: 0,
        word: 'Site(s)',
        site_ids: [],
      },
      {
        title: 'Installations',
        lastSync: null,
        disabled: true,
        url_search: '/infos', ///installations/a5b2d259-a3f0-4ac0-8f3b-55add93f2dd4/infos
        url: [],
        count: 0,
        word: 'Installation (s)',
      },
      {
        title: 'Agenda',
        lastSync: null,
        disabled: true,
        url_search: '/agendaTempos?matricule=',
        url: [],
        count: 0,
        word: 'Activité(s)',
        service: this.injector.get(AgendaTempoService),
      },
      {
        title: 'OT à réaliser d’un site',
        lastSync: null,
        disabled: true,
        url_search: 'sortField=plan_date_fin',
        url: [],
        count: 0,
        word: 'OT(s)',
      },
      {
        title: 'Historique des OTs d’un site',
        lastSync: null,
        disabled: true,
        url_search: 'statut=clos_intervenant',
        url: [],
        count: 0,
        word: 'OT(s)',
      },
      {
        title: 'Equipements',
        lastSync: null,
        disabled: true,
        url_search: '/equipements/bySite?id_site',
        url: [],
        count: 0,
        word: 'Équipement(s)',
      },
      {
        title: 'CR',
        lastSync: null,
        disabled: true,
        url_search: '/crs?id_intervenant=',
        url: [],
        count: 0,
        word: 'CR(s)',
        service: this.injector.get(ActiviteInterneService),
      },
      {
        title: 'Formulaires',
        lastSync: null,
        disabled: true,
        url_search: '/formulaires/4_400',
        url: [],
        count: 0,
        word: 'Formulaire(s)',
      },
      {
        title: 'Sites avec Relèves P1',
        lastSync: null,
        disabled: true,
        url_search: '/releve_p1/getBySite',
        url: [],
        count: 0,
        word: 'site(s) avec relève P1',
      },
    ];

    /**
     *  A terme, il faudra voir pour stocker ça dans le local storage pour ne pas
     *  perdre le lastSync
     */
    this.items_releve_P1 = [
      {
        title: 'Installations énergies',
        lastSync: null,
        disabled: true,
        url_search: '/installations/',
        params_count: 3,
        url: [],
        count: 0,
        word: 'Installation(s) énergies',
      },
      {
        title: 'Equipements énergies',
        lastSync: null,
        disabled: true,
        url_search: '/equipement',
        params_count: 4,
        url: [],
        count: 0,
        word: 'Équipement(s) énergies',
      },
    ];
  }
  /**
   * Récuperation des différentes synchronisations
   */
  getItems() {
    return this.items;
  }
  /**
   * Récuperation des différentes synchronisations
   */
  getItemsBack() {
    return this.items_releve_P1;
  }
  /**
   * Récupération de la date de la dernière synchronisation globale
   */
  getLastSync() {
    return this.lastSync;
  }

  /**
   * Lancement de la synchronisation de toutes les url
   *
   * @returns Promise
   */
  async startSynchronisation() {
    this.logger.debug('Début de la synchronisation');
    try {
      const data: InitEntry[] = await this.analyticsProvider.mesureTimePromise(
        'getInit',
        this.launchSynchronisation.bind(this)
      );
      // On met a jour la dernière synchronisation
      this.lastSync = parseInt(moment().utc().format('x'));
      if (data && data.length > 0) {
        await this.requestsProvider.removeExistingGetInitData();
        await this.processDataSynchronisation(data);
        this.logger.debug('Nombre de données synchronisées', data.length);
      }
      await this.requestsProvider.storageProviderSet('lastSynchronization', this.lastSync, false);
      this.logger.debug('Fin de la synchronisation');
    } catch (err) {
      this.logger.warn('Erreur lors de la synchronisation', err);
    }
  }

  /**
   * Traitement des réponses
   */
  async processDataSynchronisation(initEntries: InitEntry[]) {
    let urls = [];

    for (let index in this.items) {
      this.items[index].count = 0;
    }
    for (let index in this.items_releve_P1) {
      this.items_releve_P1[index].count = 0;
    }

    for (let requObj of initEntries) {
      let fullUrl = requObj.uri;
      urls.push(fullUrl);

      let storage_name = this.requestsProvider.getStorageName(fullUrl);
      let data = JSON.parse(requObj.resultat);
      if (data == null) break;
      let count = data.length;
      let each_doc = this.needToBeSplited(storage_name);
      await this.requestsProvider.storageProviderSet(storage_name, data, each_doc);
      for (let index in this.items) {
        if (fullUrl.indexOf(this.items[index].url_search) !== -1) {
          this.items[index].lastSync = this.lastSync;
          if (!this.items[index].url.includes(fullUrl)) this.items[index].url.push(fullUrl);
          if (this.items[index].title == 'Installations') {
            this.items[index].count++;
            if (this.items[2].site_ids.indexOf(data.id_localisation) === -1) {
              this.items[2].site_ids.push(data.id_localisation);
            }
          } else if (this.items[index].title == 'Formulaires') {
            this.items[index].count++;
          } else if (this.items[index].title == 'Sites' && fullUrl.indexOf('echapetBelle') === -1) {
            this.items[index].count++;
            this.items[index].count_childs += count;
            let index_localisations_id_site = fullUrl.indexOf('localisations?id_site=');
            let index_localisations_id_site_2 = fullUrl.indexOf('localisations/');
            let id_site;
            if (index_localisations_id_site !== -1) {
              id_site = fullUrl.slice(fullUrl.indexOf('localisations?id_site=') + 22);
            } else if (index_localisations_id_site_2 !== -1) {
              id_site = fullUrl.slice(fullUrl.indexOf('localisations/') + 15);
            }
            if (this.items[index].site_ids.indexOf(id_site) === -1) this.items[index].site_ids.push(id_site);
          } else if (this.items[index].title == 'Sites avec Relèves P1') {
            this.items[index].count++;
            await this.processReleveP1Items(data);
          } else {
            this.items[index].count += count;
          }
          this.items[index].disabled = false;
        }
      }
    }

    // Update last synchronisation
    this.items.forEach((item) => {
      item.lastSync = this.lastSync;
    });
    this.items_releve_P1.forEach((value) => {
      value.lastSync = this.lastSync;
    });

    const synchros: Promise<void>[] = [];
    for (const item of this.items) {
      if (item.hasOwnProperty('service')) {
        synchros.push(
          item.service
            .synchronisation()
            .toPromise()
            .then((res) => {
              item.lastSync = this.lastSync;
              item.count += res;
            })
            .catch(() => {
              item.lastSync = null;
            })
        );
      }
    }

    await Promise.all(synchros);

    return urls;
  }

  async processReleveP1Items(data: ReleveP1SiteDto) {
    let installationsActive = data.installations.filter((installation) => installation.fermee == false);

    this.items_releve_P1[0].count += installationsActive.length;
    for (let installation of installationsActive) {
      let compteursActifs = installation.compteurs
        ? installation.compteurs.filter((compteur) => compteur.actif == true)
        : [];
      let stocksActifs = installation.stocks ? installation.stocks.filter((stock) => stock.actif == true) : [];
      let circuitsActifs = installation.circuits
        ? installation.circuits.filter((circuit) => circuit.actif == true)
        : [];
      this.items_releve_P1[1].count += compteursActifs.length + stocksActifs.length + circuitsActifs.length;
    }
  }

  /**
   * Envoi de la requete init
   *
   * @returns Promise qui renvoie un array d'objets activité
   */
  async launchSynchronisation(): Promise<Array<any>> {
    try {
      // On récupère la dernière synchronisation
      let userId = this.userService.getId();
      let lastSync = await this.requestsProvider.storageProviderGet('lastSynchronization');
      let params = '?weekBefore=1&weekAfter=1';
      if (lastSync != null) params += '&timestamp=' + this.lastSync;
      let url = environment.apis.API_DACTECH_BACK_ROOT + '/v2/init/ots/intervenants/' + userId + '/init' + params;
      let data: InitEntry[] = await this.requestsProvider.getWithPulling(url);
      this.networkProvider.addHistory('Synchronisation D@cTech OK');
      return data;
    } catch (err) {
      console.warn('error startSynchronisation', err);
      this.networkProvider.addHistory('Synchronisation D@cTech KO', err);
      return [];
    }
  }

  /**
   * On vérifie si le resultat du get init doit être explosé pour chaque document
   *
   * @returns Si les données doivent être splitée dans le storage
   */
  needToBeSplited(storage_name: string) {
    return (
      storage_name.indexOf('multi_sites_gammes') < 0 &&
      storage_name.indexOf('equipement_energies') < 0 &&
      storage_name.indexOf('echapetBelle') < 0
    );
  }
}
